/**
 * Initializes and exports the rematch store. Generally, this module will only need to be modified
 * if adding a model.
 */
import LogRocket from 'logrocket';
import localforage from 'localforage';
import { init } from '@rematch/core';
import createRematchPersist from '@rematch/persist';
import selectPlugin from '@rematch/select';
import initSubscriber from 'redux-subscriber';
import { checkAttPermission } from '../modules/analytics/AppTrackingTransparency';
import { selectors as userSelectors } from './CurrentUser.model';
import models from './models';
import { initABTestModel } from './ABTest';
import storeReady, { storeReadyResolver } from './ready';
import stateManager from '../modules/user/stateManager';
import { logUserAgent } from '../api/LogUserAgent';

/**
 * Please delete this function when a sufficient number of people are on version 11.2.0 or later of
 * the iOS application. Derives user information from localStorage for migration purposes.
 */
export const deriveUserFromLocalStorage = () => {
  const ssRoot = localStorage.getItem('ssroot') || '{}';
  let userState = {};
  try {
    const ssRootParsed = JSON.parse(ssRoot);
    // ssRootParsed contains several keys mapping to stringified objects
    if (ssRootParsed.user) {
      userState = JSON.parse(ssRootParsed.user);
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[Rematch store] Could not parse root persisted item.', err);
    userState = {};
  }
  return userState;
};

/**
 * A plugin which caches data to localforage
 */
const persistPlugin = createRematchPersist(
  {
    whitelist: [
      'user',
      'providerClients',
    ], // add the key of any model you wish to have cached
    throttle: 500,
    keyPrefix: 'ss',
    version: 1,
    storage: localforage,
    writeFailHandler: err => {
      // eslint-disable-next-line no-console
      console.error('Error writing to rematch persistent storage (rematch persist plugin)', err);
    },
    migrate: (state = {}) => {
      const nextState = { ...state };
      const oldData = localStorage.getItem('ssroot');
      const oldUser = deriveUserFromLocalStorage();
      const hasMigratedUser = (state && state.user && !state.user.is_anon);

      // if the auth_token still exists in localStorage, let's set it on localforage
      // only update the persisted state if it doesn't already exist
      if (!hasMigratedUser && oldUser.auth_token) {
        nextState.user = oldUser;
      }

      // Remove state from localstorage
      if (oldData) {
        localStorage.removeItem('ssroot');
      }

      return Promise.resolve(nextState);
    },
  },
  undefined,
  () => storeReadyResolver.resolve(),
);

// this module currently gets loaded three times during bootup, so we need to cache the value to
// ensure we don't have three copies. There are some odd circular dependencies that seem to prevent
// standard module caching from working in this scenario.
const store = init({
  models,
  redux: {
    middlewares: [LogRocket.reduxMiddleware()],
  },
  plugins: [
    persistPlugin,
    selectPlugin(),
    // add any additional plugins here
  ],
});

stateManager.updateUser(store.dispatch, {});

// Initialize redux-subscriber
initSubscriber(store);

// Initialize the ab test module
initABTestModel(store);

// this is needed for unit testing in `analytics` and `profileServiceGroups` test suites
window.reduxStore = store;

store.dispatch.favorites.initialize();

store.dispatch.location.initialize();

store.dispatch.recentSearches.initialize();

storeReady.then(() => {
  store.dispatch.stripeIdentityVerification.load();
  store.dispatch.user.notifyAnalytics(null);

  if (userSelectors.isLoggedIn(store.getState())) {
    checkAttPermission();
    logUserAgent();
  }
});

export default store;
